import { memo, useEffect, useRef, useState } from 'react'
import { timelineData } from '../data/timeline'
import { GraduationCapIcon } from './icons/GraduationCapIcon'

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'career':
      return '#6366f1'
    case 'education':
      return '#10b981'
    case 'entrepreneurship':
      return '#f59e0b'
    case 'achievement':
      return '#ec4899'
    default:
      return '#6366f1'
  }
}

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'career':
      return (
        <svg
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
          className="timeline-briefcase-icon"
        >
          <defs>
            <linearGradient id="briefcaseGradientTimeline" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#d97706', stopOpacity: 1 }} />
            </linearGradient>
            <linearGradient
              id="briefcaseHandleGradientTimeline"
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop offset="0%" style={{ stopColor: '#b45309', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#92400e', stopOpacity: 1 }} />
            </linearGradient>
          </defs>

          <rect
            x="20"
            y="40"
            width="60"
            height="40"
            rx="4"
            fill="url(#briefcaseGradientTimeline)"
            className="briefcase-body"
          />
          <rect
            x="20"
            y="35"
            width="60"
            height="5"
            rx="2"
            fill="#b45309"
            className="briefcase-lid"
          />
          <path
            d="M 35 35 Q 35 25 50 25 Q 65 25 65 35"
            fill="none"
            stroke="url(#briefcaseHandleGradientTimeline)"
            strokeWidth="3"
            strokeLinecap="round"
            className="briefcase-handle"
          />

          <rect
            x="47"
            y="50"
            width="6"
            height="8"
            rx="1"
            fill="#92400e"
            className="briefcase-lock"
          />
          <circle cx="50" cy="54" r="2" fill="#fbbf24" className="lock-keyhole" />

          <rect
            x="25"
            y="43"
            width="8"
            height="3"
            rx="1"
            fill="#92400e"
            className="briefcase-clasp"
          />
          <rect
            x="67"
            y="43"
            width="8"
            height="3"
            rx="1"
            fill="#92400e"
            className="briefcase-clasp"
          />

          <line
            x1="20"
            y1="55"
            x2="80"
            y2="55"
            stroke="#d97706"
            strokeWidth="0.5"
            opacity="0.4"
            className="texture-line"
          />
          <line
            x1="20"
            y1="65"
            x2="80"
            y2="65"
            stroke="#d97706"
            strokeWidth="0.5"
            opacity="0.4"
            className="texture-line"
          />

          <circle cx="24" cy="44" r="1.5" fill="#78350f" className="corner-rivet" />
          <circle cx="76" cy="44" r="1.5" fill="#78350f" className="corner-rivet" />
          <circle cx="24" cy="76" r="1.5" fill="#78350f" className="corner-rivet" />
          <circle cx="76" cy="76" r="1.5" fill="#78350f" className="corner-rivet" />

          <circle cx="15" cy="30" r="1.5" fill="#fbbf24" className="sparkle sparkle-1" />
          <circle cx="85" cy="35" r="1" fill="#fbbf24" className="sparkle sparkle-2" />
          <circle cx="10" cy="50" r="1.2" fill="#fbbf24" className="sparkle sparkle-3" />
        </svg>
      )
    case 'education':
      return <GraduationCapIcon />
    case 'entrepreneurship':
      return '🚀'
    case 'achievement':
      return '🏆'
    default:
      return '📌'
  }
}

const arraysEqual = (left: number[], right: number[]) =>
  left.length === right.length && left.every((value, index) => value === right[index])

export const Journey = memo(function Journey() {
  const timelineRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Array<HTMLDivElement | null>>([])
  const [visibleItems, setVisibleItems] = useState<number[]>([])

  useEffect(() => {
    const items = itemRefs.current.filter((item): item is HTMLDivElement => item !== null)
    if (!items.length || typeof IntersectionObserver === 'undefined') {
      return undefined
    }

    const itemIndexes = new Map<Element, number>()
    items.forEach((item, index) => itemIndexes.set(item, index))

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleItems((current) => {
          const next = new Set(current)
          entries.forEach((entry) => {
            const index = itemIndexes.get(entry.target)
            if (index === undefined) {
              return
            }
            if (entry.isIntersecting) {
              next.add(index)
            } else {
              next.delete(index)
            }
          })
          const nextItems = Array.from(next).sort((a, b) => a - b)
          return arraysEqual(current, nextItems) ? current : nextItems
        })
      },
      { rootMargin: '-25% 0px -25% 0px', threshold: 0 }
    )

    items.forEach((item) => observer.observe(item))

    return () => observer.disconnect()
  }, [])

  return (
<section id="journey" className="journey-timeline" ref={timelineRef}>
  <div className="container-timeline">
    <div className="section-header-timeline">
      <span className="section-label-timeline">My Journey</span>
      <h2 className="section-title-timeline">Professional Timeline</h2>
      <p className="section-description-timeline">
        A decade of growth, learning, and building innovative solutions
      </p>
    </div>

    <div className="timeline-wrapper">
      <div className="timeline-line"></div>

      <div className="timeline-content">
        {timelineData.map((item, index) => (
          <div
            key={index}
            ref={(element) => { itemRefs.current[index] = element }}
                  className={`timeline-item ${visibleItems.includes(index) ? 'visible' : ''} ${item.type === 'present' ? 'present' : ''}`}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div
              className="timeline-dot"
              style={{ borderColor: getCategoryColor(item.category) }}
            >
              <span className="dot-icon">{getCategoryIcon(item.category)}</span>
            </div>

            <div className="timeline-card">
              <div className="card-header-timeline">
                <div className="card-year" style={{ color: getCategoryColor(item.category) }}>
                  {item.year}
                  {item.type === 'present' && <span className="present-badge">Present</span>}
                </div>
                <div
                  className="card-category"
                  style={{
                    background: `${getCategoryColor(item.category)}20`,
                    color: getCategoryColor(item.category),
                    borderColor: getCategoryColor(item.category),
                  }}
                >
                  {item.category}
                </div>
              </div>

              <h3 className="card-title-timeline">{item.title}</h3>
              <div className="card-company-timeline">
                {item.company} • {item.location}
              </div>

              <p className="card-description-timeline">{item.description}</p>

              <div className="card-achievements">
                {item.achievements.map((achievement, i) => (
                  <div key={i} className="achievement-item-timeline">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13 4L6 11 3 8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    {achievement}
                  </div>
                ))}
              </div>

              <div className="card-technologies">
                {item.technologies.map((tech, i) => (
                  <span
                    key={i}
                    className="tech-badge-timeline"
                    style={{ borderColor: getCategoryColor(item.category) }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
</section>
  )
})
